import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Dropdown from "../../../../parts/dropdown";
import SvgIcon from "../../../../parts/SvgIcon";
import {
  chartBars,
  compareWindow,
  daysAgo,
  doorLogsHref,
  formatLastIn,
  GYM_VISITS_RANGES,
} from "../door-visits-utils";
import LinkButton from "./LinkButton";
import RetryLabel from "./RetryLabel";

const Stat = ({ label, children, caption, small }) => (
  <div className="door-visits-stat">
    <span className="door-visits-overline">{label}</span>
    <b data-small={small || undefined}>{children}</b>
    <span className="door-visits-muted">{caption}</span>
  </div>
);

const Chart = ({ weeks, avgPerWeek, timeZone, t }) => {
  const chart = chartBars(weeks, avgPerWeek, timeZone);
  return (
    <>
      <svg
        viewBox={`0 0 ${chart.width} ${chart.height}`}
        className="door-visits-chart"
        role="img"
        aria-label={t("doorAccess.gymVisits.chartLabel")}
      >
        {chart.ticks.map((tick) => (
          <g key={tick.value}>
            <line x1="24" x2={chart.width} y1={tick.y} y2={tick.y} />
            <text x="16" y={tick.y + 4} textAnchor="end">
              {tick.value}
            </text>
          </g>
        ))}
        {chart.bars.map((bar) => (
          <g key={bar.to} data-current={bar.current || undefined}>
            <title>
              {t("doorAccess.gymVisits.weekTip", {
                from: bar.from,
                to: bar.to,
                count: bar.visits,
              })}
            </title>
            <rect
              x={bar.x}
              width={bar.width}
              rx="4"
              data-empty={bar.empty || undefined}
              y={bar.empty ? chart.emptyTop : bar.y}
              height={bar.empty ? chart.baseline - chart.emptyTop : bar.height}
            />
            <text
              className="door-visits-value"
              x={bar.center}
              y={bar.empty ? chart.baseline - 8 : bar.y - 6}
              textAnchor="middle"
              data-empty={bar.empty || undefined}
            >
              {bar.visits}
            </text>
            <text
              className="door-visits-week"
              x={bar.center}
              y={chart.height - 6}
              textAnchor="middle"
            >
              {bar.label ?? t("doorAccess.gymVisits.last7d")}
            </text>
          </g>
        ))}
        <line
          className="door-visits-average"
          x1="24"
          x2={chart.width}
          y1={chart.average}
          y2={chart.average}
        />
      </svg>
      <div className="door-visits-legend">
        <span>
          <i data-kind="bar" />
          {t("doorAccess.gymVisits.legendVisits")}
        </span>
        <span>
          <i data-kind="empty" />
          {t("doorAccess.gymVisits.legendNone")}
        </span>
        <span>
          <i data-kind="average" />
          {t("doorAccess.gymVisits.legendAverage")}
        </span>
      </div>
    </>
  );
};

const AgoText = ({ lastIn, timeZone, t }) => {
  const ago = daysAgo(lastIn, timeZone);
  return t(`doorAccess.gymVisits.ago.${ago.key}`, { count: ago.count });
};

const VisitRow = ({ event, timeZone, t }) => {
  const at = moment.tz(event.occurredAt, timeZone);
  const door = [event.door?.name, event.door?.locationName]
    .filter(Boolean)
    .join(" · ");
  return (
    <li data-denied={event.result === "denied" || undefined}>
      <span>{at.format("ddd, MMM D")}</span>
      <span>{at.format("h:mm a")}</span>
      <span className="door-visits-ellipsis door-visits-cell">
        {door || "–"}
      </span>
      <span className="door-visits-ellipsis door-visits-last">
        {event.result === "denied" ? (
          <span className="door-visits-pill">
            {event.reason
              ? t("doorAccess.result.deniedReason", { reason: event.reason })
              : t("doorAccess.result.denied")}
          </span>
        ) : (
          event.class?.name || "–"
        )}
      </span>
    </li>
  );
};

const Header = ({ t, logsHref }) => (
  <header className="door-visits-header">
    <div>
      <h3>{t("doorAccess.gymVisits.title")}</h3>
      <p className="door-visits-muted">{t("doorAccess.gymVisits.subtitle")}</p>
    </div>
    {logsHref && (
      <LinkButton type="text" small href={logsHref}>
        <span>{t("doorAccess.gymVisits.viewAll")}</span>
      </LinkButton>
    )}
  </header>
);

const refresh = (body) => submit("/actions/refresh", { method: "GET", body });

/**
 * Gym visits card on a client's profile Dashboard. `loaded` is the
 * `doorClientVisits` the profile controller loaded for this client. Hidden
 * when the gym is not on FaciliKey or the caller cannot see this client.
 * Owners and admins also get denied attempts in the list and a link to the
 * full logs.
 */
const GymVisitsCard = ({ loaded, clientId, clientName, joinedAt }) => {
  const { t } = useTranslation("coaching-portal");
  const data = loaded?.error || loaded?.clientId === clientId ? loaded : null;

  if (!data || data.forbidden || data.connected === false) return null;

  if (data.error) {
    return (
      <article className="door-visits-card">
        <Header t={t} />
        <div className="door-visits-empty">
          <p>{t("doorAccess.gymVisits.error")}</p>
          <button
            type="button"
            data-button-type="secondary"
            sz="small"
            onClick={() => refresh({})}
          >
            <RetryLabel label={t("doorAccess.gymVisits.retry")} small />
          </button>
        </div>
      </article>
    );
  }

  const { timeZone } = data;
  const twelveWeeks = data.weeks.reduce((sum, week) => sum + week.visits, 0);
  const { canSeeDenied } = data;
  const logsHref = doorLogsHref({
    memberId: clientId,
    memberName: clientName,
    range: "30d",
  });

  if (!data.hasEvents || twelveWeeks === 0) {
    const joined = joinedAt ? moment.tz(joinedAt, timeZone) : null;
    const since = joined?.isValid()
      ? joined.format(
          joined.isSame(moment.tz(timeZone), "year") ? "MMM D" : "MMM D, YYYY"
        )
      : "";
    return (
      <article className="door-visits-card">
        <Header t={t} />
        <div className="door-visits-empty" data-tone="warning">
          <SvgIcon src="/img/new-icons/warning-white.svg" useCurrentColor />
          <b>
            {data.hasEvents
              ? t("doorAccess.gymVisits.noneTitle")
              : t("doorAccess.gymVisits.noneYet")}
          </b>
          <p>
            {since
              ? t("doorAccess.gymVisits.noneSince", { date: since })
              : t("doorAccess.gymVisits.noneText")}
          </p>
        </div>
      </article>
    );
  }

  const trend = compareWindow(data.last4Weeks, data.previous4Weeks);
  const trendCaption =
    trend === "same"
      ? t("doorAccess.gymVisits.sameAsPrevious")
      : t(`doorAccess.gymVisits.${trend}From`, { count: data.previous4Weeks });

  return (
    <article className="door-visits-card">
      <Header t={t} logsHref={canSeeDenied ? logsHref : undefined} />
      <div className="door-visits-stats">
        <Stat
          label={t("doorAccess.gymVisits.thisWeek")}
          caption={t("doorAccess.gymVisits.last7Days")}
        >
          {data.thisWeek}{" "}
          <small>
            {t("doorAccess.gymVisits.visitUnit", { count: data.thisWeek })}
          </small>
        </Stat>
        <Stat
          label={t("doorAccess.gymVisits.last4Weeks")}
          caption={trendCaption}
        >
          {data.last4Weeks}
        </Stat>
        <Stat
          label={t("doorAccess.gymVisits.avgPerWeek")}
          caption={t("doorAccess.gymVisits.twelveWeeks")}
        >
          {data.avgPerWeek.toFixed(1)}
        </Stat>
        <Stat
          small
          label={t("doorAccess.gymVisits.lastIn")}
          caption={
            data.lastIn ? (
              <AgoText lastIn={data.lastIn} timeZone={timeZone} t={t} />
            ) : (
              "–"
            )
          }
        >
          {formatLastIn(data.lastIn, timeZone, true) || "–"}
        </Stat>
      </div>
      <Chart
        weeks={data.weeks}
        avgPerWeek={data.avgPerWeek}
        timeZone={timeZone}
        t={t}
      />
      <section className="door-visits-list">
        <div className="door-visits-list-head">
          <h4>{t("doorAccess.gymVisits.lastTen")}</h4>
          <Dropdown
            options={GYM_VISITS_RANGES.map((value) => ({
              value,
              text: t(`doorAccess.gymVisits.range.${value}`),
            }))}
            selectedValue={data.listRange}
            summaryText={t(`doorAccess.gymVisits.range.${data.listRange}`)}
            alignRight
            onSelectFn={(range) =>
              refresh({ visitsRange: range === "weeks12" ? "" : range })
            }
          />
        </div>
        {data.list.length ? (
          <ul>
            {data.list.map((event) => (
              <VisitRow
                key={event.id}
                event={event}
                timeZone={timeZone}
                t={t}
              />
            ))}
          </ul>
        ) : (
          <div className="door-visits-empty">
            <p>{t("doorAccess.gymVisits.noRange")}</p>
            <button
              type="button"
              data-button-type="text"
              sz="small"
              onClick={() => refresh({ visitsRange: "" })}
            >
              <span>{t("doorAccess.gymVisits.backToRange")}</span>
            </button>
          </div>
        )}
        {canSeeDenied && (
          <p className="door-visits-muted">
            {t("doorAccess.gymVisits.deniedNote")}
          </p>
        )}
      </section>
    </article>
  );
};

export default GymVisitsCard;
